import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for fetching and managing properties data
 * @param {Object} options - Configuration options
 * @param {number} [options.cityId] - Optional city ID filter
 * @param {number} [options.communityId] - Optional community ID filter
 * @param {boolean} [options.autoFetch=true] - Whether to fetch data automatically on mount
 * @returns {Object} Properties data and functions
 */
const useProperties = ({ cityId, communityId, autoFetch = true } = {}) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch properties data
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (cityId) params.city_id = cityId;
      if (communityId) params.community_id = communityId;
      
      console.log("Fetching properties with params:", params);

      const response = await api.get('/properties/', { params });
      console.log("Properties API response:", response.data);
      setProperties(response.data);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.message || 'Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  }, [cityId, communityId]);

  // Get a single property by ID
  const getProperty = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/properties/${propertyId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching property ${propertyId}:`, err);
      setError(err.message || 'Failed to fetch property');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new property
  const createProperty = useCallback(async (propertyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/properties/', propertyData);
      setProperties((prevProperties) => [...prevProperties, response.data]);
      return response.data;
    } catch (err) {
      console.error('Error creating property:', err);
      setError(err.message || 'Failed to create property');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing property
  const updateProperty = useCallback(async (propertyId, propertyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/properties/${propertyId}`, propertyData);
      setProperties((prevProperties) =>
        prevProperties.map((prop) =>
          prop.id === propertyId ? response.data : prop
        )
      );
      return response.data;
    } catch (err) {
      console.error(`Error updating property ${propertyId}:`, err);
      setError(err.message || 'Failed to update property');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a property
  const deleteProperty = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/properties/${propertyId}`);
      setProperties((prevProperties) =>
        prevProperties.filter((prop) => prop.id !== propertyId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting property ${propertyId}:`, err);
      setError(err.message || 'Failed to delete property');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchProperties();
    }
  }, [autoFetch, fetchProperties]);

  return {
    properties,
    loading,
    error,
    fetchProperties,
    getProperty,
    createProperty,
    updateProperty,
    deleteProperty,
  };
};

export default useProperties; 